import BackendService from 'BackendService';
import { Permissions } from 'Permission';
import { usePermissions } from 'PermissionContext';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DocumentCategory } from '../../../models/DocumentCategory';

/**
 * Loads the global document-category list for the upload / re-file selectors and,
 * for users who may create categories, an inline "add category" action that
 * makes the new category immediately available everywhere.
 */
export function useDocumentCategoryOptions() {
  const backendRef = useRef(new BackendService());
  const [categories, setCategories] = useState<DocumentCategory[]>([]);

  const { hasPermission } = usePermissions();
  const canCreateCategory =
    hasPermission(Permissions.DocumentCategoriesManage) ||
    hasPermission(Permissions.DocumentCategoriesAdd);

  const loadCategories = useCallback(async () => {
    try {
      setCategories(await backendRef.current.listDocumentCategories());
    } catch (err) {
      console.error('Failed to load document categories', err);
    }
  }, []);

  useEffect(() => {
    void loadCategories();
  }, [loadCategories]);

  const defaultCategoryId = useMemo(() => categories.find((c) => c.isDefault)?.id, [categories]);

  const createCategoryInline = useCallback(async (name: string): Promise<string | null> => {
    const trimmed = name.trim();
    if (!trimmed) return null;
    try {
      const created = await backendRef.current.createDocumentCategory({ name: trimmed });
      setCategories((prev) => (prev.some((c) => c.id === created.id) ? prev : [...prev, created]));
      return created.id;
    } catch (err) {
      console.error('Failed to create category', err);
      return null;
    }
  }, []);

  return {
    categories,
    defaultCategoryId,
    canCreateCategory,
    createCategoryInline,
    loadCategories,
  };
}
